import { useTranslation } from 'react-i18next';

/**
 * 節 1 つの器と、どの節を開いているかの型。
 *
 * 憲章・ナラティブ・機能一覧・バリデーション一覧・決定記録・補足欄・デザイン成果物・総合テストで同じ扱いにするために切り出す（節ごとに書き分けると、
 * 「この節だけ注意が出ない」のような差が入り込む）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の各成果物を見出し・編集ボタン・保存時の注意つきの節として並べる器
 * @graph-connects none
 */

/** 編集できる節。空文字はどれも開いていない状態。 @graph-connects none */
export type SpecSection =
  | ''
  | 'charter'
  | 'narrative'
  | 'features'
  | 'validationList'
  | 'decisionLog'
  | 'projectNotes'
  | 'designLinks';

/**
 * 節 1 つの器。見出し・編集ボタン・保存時の注意・本文を同じ形で並べる。
 * @graph-connects none
 */
export function EditableSection({
  heading,
  action,
  editing,
  warnings,
  onEdit,
  headerAction,
  version,
  children,
}: {
  heading: string;
  /** 見出しの右のボタンの文言。まだ無いものは「書く」 */
  action: string;
  editing: boolean;
  warnings: string[];
  onEdit?: () => void;
  /**
   * 見出しの右に置くものを差し替える。**別ページへ送る節だけが使う**（その節はここで編集しないので、
   * ボタンではなくリンクになる）。渡さなければ `onEdit` を呼ぶボタンが出る。
   */
  headerAction?: React.ReactNode;
  /** 見出しの隣に添える版。まだ書かれていない成果物には無い。 */
  version?: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between border-b border-[var(--color-border)] pb-1">
        <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
          <span>{heading}</span>
          {version === undefined ? null : (
            <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)] font-mono">
              v{version}
            </span>
          )}
        </h2>
        {editing
          ? null
          : (headerAction ?? (
              <button
                type="button"
                className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
                aria-label={t('spec.section.editAria', { heading, action })}
                onClick={onEdit}
              >
                {action}
              </button>
            ))}
      </div>
      {!editing && warnings.length > 0 ? (
        <div className="mt-3 border-l-4 border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-3 rounded-r">
          <h3 className="text-xs font-semibold text-[var(--color-warning-text)]">
            {t('spec.savedWithWarnings')}
          </h3>
          <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {children}
    </section>
  );
}
